import { useEffect, useMemo, useState } from 'react';
import { Pencil, Plus, Search } from 'lucide-react';
import { Button } from '../ui/Button.js';
import { Input } from '../ui/Input.js';
import { EmptyState } from './EmptyState.js';
import { ListSkeleton } from './TaskList.js';
import { useCatalog } from '../stores/catalog.js';
import { useUi } from '../stores/ui.js';
import { useShop } from '../stores/shop.js';
import { STORE_LABELS, otherStore } from '../lib/store.js';
import { useIsMobile } from '../hooks/useIsMobile.js';
import { normalize } from '../lib/catalog-search.js';

/**
 * Panneau « Catalogue » de la page Courses (ADR 0007). Données de référence lues par
 * `CourseAddModal` — jamais consultées pour elles-mêmes, donc pas une destination de nav.
 * Reste en lecture seule sur mobile, comme avant le déménagement.
 */
export function CatalogPanel(): React.ReactElement {
  const items = useCatalog((s) => s.items);
  const loading = useCatalog((s) => s.loading);
  const error = useCatalog((s) => s.error);
  const loadedStore = useCatalog((s) => s.loadedStore);
  const otherCount = useCatalog((s) => s.otherCount);
  const load = useCatalog((s) => s.load);
  const loadOtherCount = useCatalog((s) => s.loadOtherCount);
  const openForm = useUi((s) => s.openCatalogForm);
  const store = useShop((s) => s.store);
  const setStore = useShop((s) => s.setStore);
  const isMobile = useIsMobile();

  const [query, setQuery] = useState('');

  // « Déjà chargé » n'a de sens que pour un magasin donné : changer de magasin recharge.
  useEffect(() => {
    if (loadedStore !== store) load();
  }, [loadedStore, store, load]);

  // Le second GET de l'empty state, et seulement là : dire où sont les items évite le
  // « il a tout perdu » quand on arrive sur le magasin par défaut, encore vide.
  useEffect(() => {
    if (!loading && loadedStore === store && items.length === 0 && otherCount === null && !error) {
      loadOtherCount();
    }
  }, [loading, loadedStore, store, items.length, otherCount, error, loadOtherCount]);

  const filtered = useMemo(() => {
    const q = normalize(query.trim());
    if (!q) return items;
    return items.filter((it) => normalize(it.name).includes(q));
  }, [items, query]);

  return (
    <div className="space-y-4">
      {!isMobile && (
        <div className="flex justify-end">
          <Button onClick={() => openForm()}>
            <Plus size={16} aria-hidden />
            Nouvel item
          </Button>
        </div>
      )}

      {isMobile && (
        <p className="alerte alerte-sobre text-sm">
          L'édition du catalogue se fait sur desktop. Tu peux consulter tes items ici.
        </p>
      )}

      <div className="relative">
        <Search
          size={16}
          aria-hidden
          className="absolute left-3 top-1/2 -translate-y-1/2 text-muted pointer-events-none"
        />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Rechercher un item…"
          aria-label="Rechercher un item"
          className="pl-10"
        />
      </div>

      {error ? (
        <div className="alerte alerte-erreur pile pile-serree" role="alert">
          <span>
            Impossible de charger le catalogue {STORE_LABELS[store]} · {error}
          </span>
          <Button variant="secondary" size="small" className="self-start" onClick={() => load()}>
            Réessayer
          </Button>
        </div>
      ) : loading && items.length === 0 ? (
        <ListSkeleton />
      ) : items.length === 0 ? (
        <div className="vide">
          <p className="vide-titre">Aucun item dans le catalogue {STORE_LABELS[store]}</p>
          {otherCount !== null && otherCount > 0 ? (
            <p>
              {otherCount} item{otherCount > 1 ? 's sont rangés' : ' est rangé'} dans{' '}
              <button
                type="button"
                onClick={() => setStore(otherStore(store))}
                className="underline text-primary-ink"
              >
                {STORE_LABELS[otherStore(store)]}
              </button>
              .
            </p>
          ) : (
            <p>Crée ton premier item pour réutiliser des produits courants (lessive, eau, …).</p>
          )}
          {!isMobile && (
            <Button onClick={() => openForm()}>
              <Plus size={16} aria-hidden />
              Nouvel item
            </Button>
          )}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState>Aucun résultat pour &laquo;&nbsp;{query}&nbsp;&raquo;</EmptyState>
      ) : (
        <div className="liste liste-aeree">
          {filtered.map((it) => {
            const contenu = (
              <>
                <span className="ligne-corps">
                  <span className="ligne-titre">{it.name}</span>
                </span>
                {it.defaultQty > 1 && <span className="ligne-repere">×{it.defaultQty}</span>}
                <span className="etiquette">
                  {it.urls.length} URL{it.urls.length > 1 ? 's' : ''}
                </span>
              </>
            );
            // En lecture seule sur mobile : une ligne, pas un bouton désactivé.
            return isMobile ? (
              <div key={it.itemId} className="ligne ligne-bordee">
                {contenu}
              </div>
            ) : (
              <button
                key={it.itemId}
                type="button"
                onClick={() => openForm(it.itemId)}
                aria-label={`Modifier ${it.name}`}
                className="ligne ligne-bordee ligne-cliquable"
              >
                {contenu}
                <Pencil size={14} aria-hidden className="shrink-0 text-muted" />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
